import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { UserPlus, Search, Link2, RefreshCw, Trash2, X, Copy } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { StatusPill } from '../../components/ui';
import { useClientRecord, ClientFrame } from './ClientLayout';

const PORTAL_STATUS = {
  active: { label: 'Active', tone: 'success' },
  pending: { label: 'Invite pending', tone: 'warning' },
  revoked: { label: 'Revoked', tone: 'neutral' },
};

export default function ClientFamilyPortal() {
  const { id } = useParams();
  const record = useClientRecord(id);
  const confirm = useConfirm();
  const [invites, setInvites]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [status, setStatus]     = useState('All');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState({ name: '', email: '', relationship: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/clients/${id}/family-portal`)
      .then(r => setInvites(r.data || []))
      .catch(() => setInvites([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [id]);

  const invite = async e => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post(`/clients/${id}/family-portal/invite`, form);
      toast.success(`Invitation sent to ${form.email}`);
      if (res.data.inviteUrl) {
        await navigator.clipboard.writeText(res.data.inviteUrl);
        toast.success('Invite link copied to clipboard!');
      }
      setShowForm(false);
      setForm({ name: '', email: '', relationship: '', phone: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to send invite'); }
    finally { setSubmitting(false); }
  };

  const revoke = async (memberId, name) => {
    if (!await confirm(`${name} can no longer sign in to the family portal for this client.`, { title: `Revoke access for ${name}?`, danger: true, confirmLabel: 'Revoke access' })) return;
    try {
      await api.delete(`/clients/${id}/family-portal/${memberId}`);
      toast.success('Access revoked');
      load();
    } catch { toast.error('Failed to revoke'); }
  };

  const resend = async (memberId) => {
    try {
      const res = await api.post(`/family-portal/${memberId}/resend`);
      if (res.data.inviteUrl) {
        await navigator.clipboard.writeText(res.data.inviteUrl);
        toast.success('New invite link copied to clipboard!');
      }
      load();
    } catch { toast.error('Failed to resend invite'); }
  };

  const copyLink = async (memberId) => {
    try {
      const res = await api.get(`/family-portal/${memberId}/link`);
      await navigator.clipboard.writeText(res.data.url);
      toast.success('Invite link copied to clipboard!');
    } catch { toast.error('Failed to get link'); }
  };

  const filtered = invites.filter(i => {
    if (search && !`${i.name} ${i.email}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (status !== 'All' && i.status !== status.toLowerCase()) return false;
    return true;
  });

  return (
    <ClientFrame record={record}>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Family portal</h2>
          <p className="text-[13px] text-slate-500 mt-0.5">Invite family members to view this client's care details.</p>
        </div>
        <button type="button" onClick={() => setShowForm(true)} className="btn-primary">
          <UserPlus size={15} aria-hidden /> Invite family member
        </button>
      </div>

      {/* Invite Form */}
      {showForm && (
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-700 text-sm">Send an invitation</h3>
            <button type="button" onClick={() => setShowForm(false)} aria-label="Close the invitation form" className="p-2 -m-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"><X size={16} aria-hidden /></button>
          </div>
          <form onSubmit={invite} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="fp-name" className="input-label">Full name *</label>
              <input id="fp-name" className="input-field" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
            </div>
            <div>
              <label htmlFor="fp-email" className="input-label">Email *</label>
              <input id="fp-email" type="email" className="input-field" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
            </div>
            <div>
              <label htmlFor="fp-rel" className="input-label">Relationship</label>
              <select id="fp-rel" className="input-field" value={form.relationship} onChange={e => setForm(f => ({ ...f, relationship: e.target.value }))}>
                <option value="">Select…</option>
                <option>Parent</option><option>Child</option><option>Spouse/Partner</option>
                <option>Sibling</option><option>Guardian</option><option>Carer</option><option>Other</option>
              </select>
            </div>
            <div>
              <label htmlFor="fp-phone" className="input-label">Phone</label>
              <input id="fp-phone" type="tel" className="input-field" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
            </div>
            <div className="sm:col-span-2 flex gap-2 justify-end">
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary text-sm">Cancel</button>
              <button type="submit" disabled={submitting} className="btn-primary text-sm">
                {submitting ? 'Sending…' : 'Send invite and copy link'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} aria-label="Search family members"
              className="w-full border border-slate-200 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:border-primary-400"
              placeholder="Search by name or email" />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="fp-status" className="text-xs text-slate-500">Status</label>
            <select id="fp-status" value={status} onChange={e => setStatus(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none w-32">
              <option>All</option><option>Active</option><option>Pending</option><option>Revoked</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white border-b border-slate-200 text-xs text-slate-600">
              <tr>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Relationship</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Joined</th>
                <th className="px-4 py-3 text-left">Last access</th>
                <th className="px-4 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={7} className="text-center py-10">
                  <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
                </td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-10 text-slate-500">
                  {invites.length ? 'No family members match' : 'No family members invited yet. Invite one so they can follow this client’s care.'}
                </td></tr>
              ) : filtered.map(inv => (
                <tr key={inv.id} className="hover:bg-slate-50/60">
                  <td className="px-4 py-3 font-medium text-slate-700">{inv.name}</td>
                  <td className="px-4 py-3 text-slate-500">{inv.email}</td>
                  <td className="px-4 py-3 text-slate-500">{inv.relationship || '—'}</td>
                  <td className="px-4 py-3">
                    <StatusPill status={inv.status || 'pending'} map={PORTAL_STATUS} />
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{inv.joined_at?.split('T')[0] || '—'}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">
                    {inv.last_accessed_at ? new Date(inv.last_accessed_at).toLocaleString('en-AU') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {inv.status !== 'revoked' && (
                        <>
                          <button type="button" onClick={() => copyLink(inv.id)} title="Copy invite link" aria-label={`Copy invite link for ${inv.name}`}
                            className="w-9 h-9 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors">
                            <Copy size={14} aria-hidden />
                          </button>
                          {inv.status === 'pending' && (
                            <button type="button" onClick={() => resend(inv.id)} title="Resend invite" aria-label={`Resend invite to ${inv.name}`}
                              className="w-9 h-9 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors">
                              <RefreshCw size={14} aria-hidden />
                            </button>
                          )}
                          <button type="button" onClick={() => revoke(inv.id, inv.name)} title="Revoke access" aria-label={`Revoke access for ${inv.name}`}
                            className="w-9 h-9 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors">
                            <Trash2 size={14} aria-hidden />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-500">
          {filtered.length} member{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>
    </ClientFrame>
  );
}
